import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getShippingProfiles,
  getShippingAssignments,
} from "../utils/shippingApi";

/**
 * Manages assignments for BOTH persisted products and local drafts.
 * - Keeps assignments keyed by productId (string) where productId can be a draft id (nanoid).
 * - Can serialize for server by translating draft ids via an idMap (local->server).
 */
export function useShippingAssignments(businessId, products) {
  const [profiles, setProfiles] = useState([]);
  const [assignments, setAssignments] = useState({}); // { [profileName]: string[] productIds }
  const [flags, setFlags] = useState({}); // { [productId]: { delivery, delivery_requires_approval, takeaway, takeaway_requires_approval } }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [baseCurrency, setBaseCurrency] = useState(null);
  // Snapshot of last server state (only for persisted ids)
  const serverSnapshotRef = useRef({ assignments: {}, flags: {} });

const reload = useCallback(async () => {
  if (!businessId) {
    setProfiles([]);
    setBaseCurrency("USD");
    setAssignments({});
    setFlags({});
    serverSnapshotRef.current = { assignments: {}, flags: {} };
    return;
  }

  setError(null);
  try {
    const [profileData, a] = await Promise.all([
      getShippingProfiles(businessId),
      getShippingAssignments(businessId),
    ]);
console.log("Shipping profiles loaded:", profileData);
    setProfiles(profileData.shippingGroups);
    setBaseCurrency(profileData.baseCurrency);
    const aMap = a?.assignments || {};
    const fMap = a?.flags || {};
    setAssignments(aMap);
    setFlags(fMap);
    serverSnapshotRef.current = { assignments: aMap, flags: fMap };
  } catch (e) {
    setProfiles([]);
    setBaseCurrency("USD");
    setAssignments({});
    setFlags({});
    serverSnapshotRef.current = { assignments: {}, flags: {} };
    setError("Could not load shipping data");
  }
}, [businessId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Keep assignments/flags pruned to existing product ids (including drafts)
  const productIds = useMemo(() => (products || []).map((p) => String(p.id)), [products]);
  useEffect(() => {
    // prune flags for removed drafts and removed products
    setFlags((prev) => {
      const next = {};
      for (const [pid, val] of Object.entries(prev)) {
        if (productIds.includes(pid)) next[pid] = val;
      }
      return next;
    });
    // prune assignments
    setAssignments((prev) => {
      const out = {};
      for (const [profile, list] of Object.entries(prev)) {
        out[profile] = (list || []).filter((pid) => productIds.includes(pid));
      }
      return out;
    });
  }, [productIds]);

  // Mutators -----------------------------------------------------------------
  const toggleProductForProfile = (profileName, productId, on) => {
    setAssignments((prev) => {
      const list = new Set(prev[profileName] || []);
      on ? list.add(String(productId)) : list.delete(String(productId));
      return { ...prev, [profileName]: Array.from(list) };
    });
  };

  const setAllForProfile = (profileName, on) => {
    setAssignments((prev) => ({
      ...prev,
      [profileName]: on ? productIds : [],
    }));
  };

  const setAllForCategory = (profileName, categoryProductIds = [], on) => {
    setAssignments((prev) => {
      const current = new Set(prev[profileName] || []);
      categoryProductIds.forEach((id) =>
        on ? current.add(String(id)) : current.delete(String(id))
      );
      return { ...prev, [profileName]: Array.from(current) };
    });
  };

  const setFlag = (productId, key, val) => {
    setFlags((prev) => ({
      ...prev,
      [productId]: {
        delivery: false,
        delivery_requires_approval: false,
        takeaway: false,
        takeaway_requires_approval: false,
        ...(prev[productId] || {}),
        [key]: val,
      },
    }));
  };

  /**
   * Set delivery/takeaway flags for one product in a single state update.
   * channel: "delivery" | "takeaway"
   * enabled: boolean
   * requireApproval: boolean (only used when enabled=true)
   */
  const setChannelFor = (productId, channel, enabled, requireApproval) => {
    const c = channel === "takeaway" ? "takeaway" : "delivery";
    setFlags((prev) => {
      const prevF = prev[productId] || {};
      const patch =
        c === "delivery"
          ? { delivery: !!enabled, delivery_requires_approval: !!enabled && !!requireApproval }
          : { takeaway: !!enabled, takeaway_requires_approval: !!enabled && !!requireApproval };
      return {
        ...prev,
        [productId]: {
          delivery: prevF.delivery || false,
          delivery_requires_approval: prevF.delivery_requires_approval || false,
          takeaway: prevF.takeaway || false,
          takeaway_requires_approval: prevF.takeaway_requires_approval || false,
          ...patch,
        },
      };
    });
  };

  /**
   * Bulk-apply channel flags for many products.
   */
  const setChannelForMany = (ids = [], channel, enabled, requireApproval) => {
    const list = (ids || []).map(String);
    const c = channel === "takeaway" ? "takeaway" : "delivery";
    setFlags((prev) => {
      const next = { ...prev };
      for (const pid of list) {
        const prevF = next[pid] || {};
        const patch =
          c === "delivery"
            ? { delivery: !!enabled, delivery_requires_approval: !!enabled && !!requireApproval }
            : { takeaway: !!enabled, takeaway_requires_approval: !!enabled && !!requireApproval };
        next[pid] = {
          delivery: prevF.delivery || false,
          delivery_requires_approval: prevF.delivery_requires_approval || false,
          takeaway: prevF.takeaway || false,
          takeaway_requires_approval: prevF.takeaway_requires_approval || false,
          ...patch,
        };
      }
      return next;
    });
  };

  // Dirty state / counts -----------------------------------------------------
  function normalizeForCompareOnlyPersisted(assignmentsLike, flagsLike) {
    // keep only server-known ids (those that were in the snapshot), so we can diff real server state
    const persistedIds = new Set(
      Object.values(serverSnapshotRef.current.assignments).flat()
    );
    const outA = {};
    for (const [profile, list] of Object.entries(assignmentsLike || {})) {
      outA[profile] = (list || []).filter((id) => persistedIds.has(id)).sort();
    }
    const outF = {};
    for (const [pid, f] of Object.entries(flagsLike || {})) {
      if (persistedIds.has(pid)) outF[pid] = f || {};
    }
    return { assignments: outA, flags: outF };
  }

  const pendingCounts = useMemo(() => {
    // Count: (1) changes vs server for persisted ids, (2) any usage of draft ids
    const persistedNow = normalizeForCompareOnlyPersisted(assignments, flags);
    const persistedPrev = normalizeForCompareOnlyPersisted(
      serverSnapshotRef.current.assignments,
      serverSnapshotRef.current.flags
    );
    const changedPersisted =
      JSON.stringify(persistedNow) !== JSON.stringify(persistedPrev);

    const allIdsInAssignments = new Set(Object.values(assignments).flat());
    const hasDraftIds = Array.from(allIdsInAssignments).some(
      (id) => !Object.values(serverSnapshotRef.current.assignments).flat().includes(id)
    );

    // Count products that have ANY true flag (delivery or takeaway)
    const flaggedCount = Object.values(flags).reduce((acc, f = {}) => {
      const any =
        f.delivery ||
        f.takeaway ||
        f.delivery_requires_approval ||
        f.takeaway_requires_approval;
      return acc + (any ? 1 : 0);
    }, 0);

    return {
      changedPersisted,
      hasDraftIds,
      flaggedCount,
      totalAssignmentSelections: Array.from(allIdsInAssignments).length,
    };
  }, [assignments, flags]);

  const hasChanges =
    pendingCounts.changedPersisted ||
    pendingCounts.hasDraftIds ||
    pendingCounts.flaggedCount > 0;

  function validateBusinessRules(productsList = []) {
    const list = Array.isArray(productsList) ? productsList : [];
    const byId = new Map(list.map((p) => [String(p.id), p]));

    const missingFulfillment = [];
    const channelConflicts = [];
    const missingDeliveryProfile = [];

    const productIdsAll = list.map((p) => String(p.id));

    for (const pid of productIdsAll) {
      const f = flags[pid] || {};
      const inDelivery = !!f.delivery;
      const inTakeaway = !!f.takeaway;

      // must be in at least one channel
      if (!inDelivery && !inTakeaway) {
        missingFulfillment.push(pid);
      }

      // defensively check impossible conflict: manual & auto simultaneously (same channel)
      if (inDelivery && typeof f.delivery_requires_approval !== "boolean") {
        channelConflicts.push(pid);
      }
      if (inTakeaway && typeof f.takeaway_requires_approval !== "boolean") {
        channelConflicts.push(pid);
      }

      // delivery -> must be in at least one shipping profile
      if (inDelivery) {
        let inAnyProfile = false;
        for (const list of Object.values(assignments || {})) {
          if (Array.isArray(list) && list.includes(pid)) {
            inAnyProfile = true;
            break;
          }
        }
        if (!inAnyProfile) missingDeliveryProfile.push(pid);
      }
    }

    const msgs = [];
    if (missingFulfillment.length) {
      msgs.push(
        `Some products have no fulfillment: ${missingFulfillment
          .map((id) => byId.get(id)?.name || id)
          .join(", ")}. Choose Delivery and/or Takeaway.`
      );
    }
    if (channelConflicts.length) {
      msgs.push(
        `Conflicting approval settings on: ${channelConflicts
          .map((id) => byId.get(id)?.name || id)
          .join(", ")}. A channel must be either manual OR no approval.`
      );
    }
    if (missingDeliveryProfile.length) {
      msgs.push(
        `Delivery products missing a shipping profile: ${missingDeliveryProfile
          .map((id) => byId.get(id)?.name || id)
          .join(", ")}. Assign at least one profile.`
      );
    }

    return { ok: msgs.length === 0, messages: msgs, details: { missingFulfillment, channelConflicts, missingDeliveryProfile } };
  }

  return {
    profiles,
    baseCurrency,
    assignments,
    flags,
    busy,
    error,
    validateBusinessRules,
    setError,
    reload,
    hasChanges,
    pendingCounts,
    // profile assignment controls
    toggleProductForProfile,
    setAllForProfile,
    setAllForCategory,
    // fulfillment flags controls
    setFlag,
    setChannelFor,
    setChannelForMany,
    // save
  };
}
